"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

export interface FaqItem {
  question: string;
  answer: ReactNode;
}

export interface FaqCategory {
  id: string;
  label: string;
  items: FaqItem[];
}

interface FaqLayoutProps {
  role: "user" | "brand";
  title: string;
  categories: FaqCategory[];
}

function FaqAccordionItem({ item }: { item: FaqItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b last:border-b-0">
      <button
        type="button"
        className="flex w-full items-center justify-between py-4 text-left text-sm font-medium hover:text-foreground"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{item.question}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>
      {isOpen && (
        <div className="pb-6 text-sm text-muted-foreground leading-relaxed">{item.answer}</div>
      )}
    </div>
  );
}

export function FaqLayout({ role, title, categories }: FaqLayoutProps) {
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id ?? "");
  const currentCategory = categories.find((c) => c.id === activeCategory) ?? categories[0];

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <h1 className="text-xl font-medium mb-6">{title}</h1>

      {/* Role toggle: 구매자 / 브랜드 */}
      <div className="flex gap-2 mb-6">
        <Link
          href="/faq/user"
          className={cn(
            "px-4 py-2 text-sm rounded-sm border transition-colors",
            role === "user"
              ? "bg-neutral-900 text-white border-neutral-900"
              : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400",
          )}
        >
          제품을 구매하는 사장님
        </Link>
        <Link
          href="/faq/brand"
          className={cn(
            "px-4 py-2 text-sm rounded-sm border transition-colors",
            role === "brand"
              ? "bg-neutral-900 text-white border-neutral-900"
              : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400",
          )}
        >
          단길에 입점한 브랜드
        </Link>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setActiveCategory(cat.id)}
            className={cn(
              "px-3 py-1.5 text-[13px] rounded-sm border transition-colors whitespace-nowrap",
              activeCategory === cat.id
                ? "bg-neutral-900 text-white border-neutral-900"
                : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400",
            )}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* FAQ items */}
      <div className="border rounded-md px-4">
        {currentCategory?.items.map((item, i) => (
          <FaqAccordionItem key={i} item={item} />
        ))}
      </div>
    </div>
  );
}
